import { useEffect, useState } from "react";

/**
 * Арабский интерфейс сайта — один рубильник.
 *
 * Владелец, 11.10: «арабский пока не обязателен, подготовь перевод и всё
 * остальное, но язык пока не ставь». Поэтому:
 *  - AR_UI_ENABLED = false (сейчас): /ar — английский интерфейс, как и было
 *    (решение D15); в выборе языка арабского нет; locales/ar.json даже не
 *    скачивается;
 *  - AR_UI_ENABLED = true: /ar по-арабски — строки locales/ar.json (чего там
 *    нет — английский), <html lang="ar" dir="rtl">, MUI справа налево
 *    (shared/lib/rtl), арабский в выборе языка.
 *
 * Предпросмотр для проверки, пока рубильник выключен, — на одном устройстве:
 * админ открывает любой адрес с ?ar_preview=1 (или нажимает плитку
 * «Арабский интерфейс (предпросмотр)» в меню профиля) — браузер запоминает
 * ключ AR_PREVIEW_KEY, и /ar на этом устройстве арабский; ?ar_preview=0 или та
 * же плитка — выключить. Ключ ставит только админ (ArabicPreviewParam), обычный
 * человек по такой ссылке ничего не меняет.
 */
export const AR_UI_ENABLED = false;

/** Ключ localStorage предпросмотра: "1" — на этом устройстве /ar арабский. */
export const AR_PREVIEW_KEY = "ar_ui_preview";

/** Параметр адреса: ?ar_preview=1 — включить предпросмотр (админу), 0 — выключить. */
export const AR_PREVIEW_PARAM = "ar_preview";

/**
 * Числа и даты на арабском (Intl): арабские слова, западные цифры 0–9 и
 * григорианский календарь. Цены, баллы, проценты и даты читаются так же, как на
 * остальных языках сайта и в приложениях; голое «ar» старые Safari и часть
 * Android рисуют восточно-арабскими цифрами (١٢٣), а «ar-SA» ещё и с хиджрой.
 */
export const AR_INTL_LOCALE = "ar-u-nu-latn";

const listeners = new Set<(on: boolean) => void>();

export const getArPreview = (): boolean => {
    try {
        return localStorage.getItem(AR_PREVIEW_KEY) === "1";
    } catch {
        return false;
    }
};

export const setArPreview = (on: boolean): void => {
    try {
        if (on) localStorage.setItem(AR_PREVIEW_KEY, "1");
        else localStorage.removeItem(AR_PREVIEW_KEY);
    } catch {
        /* приватный режим — предпросмотр просто не запомнится */
    }
    listeners.forEach((fn) => fn(on));
};

/** Подписка на переключение предпросмотра вне React; возвращает отписку. */
export const subscribeArPreview = (fn: (on: boolean) => void): (() => void) => {
    listeners.add(fn);
    return () => {
        listeners.delete(fn);
    };
};

/** Арабский интерфейс включён: всем (рубильник) или на этом устройстве (предпросмотр). */
export const arUiActive = (): boolean => AR_UI_ENABLED || getArPreview();

const isAr = (lang: string | null | undefined): boolean => (lang || "").toLowerCase() === "ar";

/**
 * Страница на этом языке рисуется по-арабски. Для словарей в коде фич: пока
 * false, их нормализаторы языка (shellLang, planLang, …) отдают на /ar
 * английский, как сегодня; когда в словаре появится `ar` — ветка
 * `if (isArabicUi(lang)) return "ar"`.
 */
export const isArabicUi = (lang: string | null | undefined): boolean => isAr(lang) && arUiActive();

/** Живой признак предпросмотра: плитка, ?ar_preview, другая вкладка. */
export const useArPreview = (): boolean => {
    const [on, setOn] = useState(getArPreview);
    useEffect(() => {
        const unsubscribe = subscribeArPreview(setOn);
        const onStorage = (e: StorageEvent) => {
            if (e.key === AR_PREVIEW_KEY || e.key === null) setOn(getArPreview());
        };
        window.addEventListener("storage", onStorage);
        // ключ мог смениться между первым рисунком и подпиской
        setOn(getArPreview());
        return () => {
            unsubscribe();
            window.removeEventListener("storage", onStorage);
        };
    }, []);
    return on;
};

/** Живой isArabicUi для языка страницы. */
export const useArabicUi = (lang: string | null | undefined): boolean => {
    const preview = useArPreview();
    return isAr(lang) && (AR_UI_ENABLED || preview);
};

/** ?ar_preview=1 → true, ?ar_preview=0 → false, нет параметра → null. */
export const arPreviewFromSearch = (search: string | null | undefined): boolean | null => {
    let v: string | null = null;
    try {
        v = new URLSearchParams(search || "").get(AR_PREVIEW_PARAM);
    } catch {
        return null;
    }
    if (v === "1" || v === "true") return true;
    if (v === "0" || v === "false") return false;
    return null;
};
